import apiClient from './client'
import {
  User,
  GameRecord,
  AdminMessage,
  GuessNumberStatus,
  MorseStatus,
  MorseGuessResult,
  LuckyNumbersStatus,
  LuckyPickResult,
  GuessResult,
  RewardCode,
  GiftPrize,
  AirdropStatusResponse,
  AirdropLeaderboardEntry,
  CoinMap,
  CoinRateCents,
  CoinExchangeRow,
  FootballMatch,
  FootballClub,
  MatchPrediction,
  FantasyPlayer,
  FantasyTeam,
  FantasyTeamPlayer,
  TeamVote,
  MatchVotingInfo,
  TeamExploreItem,
  CoinTransfer,
  FantasyTeamCreateDTO,
  VoteDTO,
  AdminStats,
  PlayerGroup,
  MatchFantasyPool,
  FootballLeague,
  UserFootballScore,
  DreamTeam,
  DreamTeamExploreItem,
  DreamTeamVote,
  RaffleWinner,
  LeagueDayGoalsStatus,
  LeagueDayGoalsPrediction,
  DreamTeamCreateDTO,
  FootballGameMode,
  MatchPlayerStat,
  UserLeagueScore,
  HorseCourse,
  HorsePollRound,
  HorsePollEntry,
  HorseHistoryItem,
  HorseEntryPayment,
  HorseEntryPaymentStatus,
  UserHorseLeagueScore,
  HorsePollMode,
  HorseCourseUsage,
  SupportTicket,
  SupportMessage,
  AdminUserDetail,
} from '../types'

export interface AuthResponse {
  token: string
  refresh_token?: string
  user_id: number
  internal_user_id?: number
  is_new_user: boolean
}

export interface TelegramAuthResponse {
  message: string
  code: number
  is_new_user: boolean
  referral_url?: string
  user_info?: {
    id: number
    first_name: string
    last_name?: string
    username?: string
    photo_url?: string
    language_code?: string
  }
  token?: string
  refresh_token?: string
  user_id?: number
  internal_user_id?: number
}

export interface ReferralCodeResponse {
  code: string
  referral_url?: string
  system_referral_code?: string
  bot_username?: string
}

export const authApi = {
  telegramAuth: async (initData: string): Promise<AuthResponse | TelegramAuthResponse> => {
    try {
      const res = await apiClient.post('/auth/telegram', { init_data: initData })
      if (res.data.token) {
        localStorage.setItem('access_token', res.data.token)
        if (res.data.refresh_token) localStorage.setItem('refresh_token', res.data.refresh_token)
        localStorage.setItem('user_id', String(res.data.user_id))
      }
      return res.data
    } catch (error: any) {
      if (error.response?.status === 404) {
        return error.response.data
      }
      throw error
    }
  },

  login: async (telegramId: number): Promise<AuthResponse> => {
    const res = await apiClient.post('/auth/login', { telegram_id: telegramId })
    const data: AuthResponse = res.data
    localStorage.setItem('access_token', data.token)
    if (data.refresh_token) localStorage.setItem('refresh_token', data.refresh_token)
    localStorage.setItem('user_id', String(data.user_id))
    return data
  },

  register: async (data: {
    telegram_id: number
    username: string
    first_name: string
    last_name: string
    photo_url: string
    referral_code: string
  }, initData: string): Promise<AuthResponse> => {
    const res = await apiClient.post('/auth/register', { ...data, init_data: initData })
    const response: AuthResponse = res.data
    localStorage.setItem('access_token', response.token)
    if (response.refresh_token) localStorage.setItem('refresh_token', response.refresh_token)
    localStorage.setItem('user_id', String(response.user_id))
    return response
  },

  refresh: async (refreshToken: string): Promise<AuthResponse> => {
    const res = await apiClient.post<AuthResponse>('/auth/refresh', { refresh_token: refreshToken })
    return res.data
  },

  getRandomReferralCode: async (): Promise<ReferralCodeResponse> => {
    const res = await apiClient.get<{
      referral_code: string
      referral_url?: string
      system_referral_code?: string
      bot_username?: string
    }>('/referral/random-code')
    return {
      code: res.data.referral_code,
      referral_url: res.data.referral_url || '',
      system_referral_code: (res.data as any).system_referral_code || (res.data as any).systemReferralCode || '',
      bot_username: res.data.bot_username || '',
    }
  },

  healthCheck: async (): Promise<{ status: string }> => {
    const res = await apiClient.get('/health')
    return res.data
  },
}

export const userApi = {
  getProfile: async (): Promise<User> => {
    const res = await apiClient.get<{ user: User }>('/users/me')
    return res.data.user
  },

  getTopReferrers: async (limit: number = 10): Promise<{ users: User[] }> => {
    const res = await apiClient.get<{ users: User[] }>(`/users/top-referrers?limit=${limit}`)
    return res.data
  },

  getUnreadMessages: async (): Promise<{ messages: AdminMessage[] }> => {
    const res = await apiClient.get<{ messages: AdminMessage[] }>('/messages/unread')
    return res.data
  },

  getAllMessages: async (): Promise<{ messages: AdminMessage[] }> => {
    const res = await apiClient.get<{ messages: AdminMessage[] }>('/messages/all')
    return res.data
  },

  markMessagesRead: async (): Promise<{ message: string }> => {
    const res = await apiClient.patch('/messages/read-all')
    return res.data
  },
}

export const gameApi = {
  getGuessNumberStatus: async (): Promise<GuessNumberStatus> => {
    const res = await apiClient.get<GuessNumberStatus>('/games/guess-number/status')
    return res.data
  },

  submitGuess: async (guessNumber: string): Promise<GuessResult> => {
    const res = await apiClient.post<GuessResult>('/games/guess-number/guess', {
      guess_number: guessNumber,
    })
    return res.data
  },

  getGuessNumberWinners: async (): Promise<{ winners: GameRecord[] }> => {
    const res = await apiClient.get<{ winners: GameRecord[] }>('/games/guess-number/winners')
    return res.data
  },

  getMorseStatus: async (): Promise<MorseStatus> => {
    const res = await apiClient.get<MorseStatus>('/games/morse/status')
    return res.data
  },

  startMorse: async (): Promise<MorseStatus> => {
    const res = await apiClient.post<MorseStatus>('/games/morse/start')
    return res.data
  },

  guessMorse: async (word: string): Promise<MorseGuessResult> => {
    const res = await apiClient.post<MorseGuessResult>('/games/morse/guess', { word })
    return res.data
  },

  getLuckyNumbersStatus: async (): Promise<LuckyNumbersStatus> => {
    const res = await apiClient.get<LuckyNumbersStatus>('/games/lucky-numbers/status')
    return res.data
  },

  startLuckyNumbers: async (): Promise<LuckyNumbersStatus> => {
    const res = await apiClient.post<LuckyNumbersStatus>('/games/lucky-numbers/start')
    return res.data
  },

  submitLuckyPick: async (selected: number[]): Promise<LuckyPickResult> => {
    const res = await apiClient.post<LuckyPickResult>('/games/lucky-numbers/pick', { selected })
    return res.data
  },

  claimLuckyAdReward: async (): Promise<LuckyNumbersStatus> => {
    const res = await apiClient.post<LuckyNumbersStatus>('/games/lucky-numbers/watch-ad')
    return res.data
  },
}

export const airdropApi = {
  getStatus: async (): Promise<AirdropStatusResponse> => {
    const res = await apiClient.get<AirdropStatusResponse>('/airdrop/status')
    return res.data
  },

  startPresence: async (): Promise<AirdropStatusResponse> => {
    const res = await apiClient.post<AirdropStatusResponse>('/airdrop/presence/start')
    return res.data
  },

  heartbeatPresence: async (): Promise<AirdropStatusResponse> => {
    const res = await apiClient.post<AirdropStatusResponse>('/airdrop/presence/heartbeat')
    return res.data
  },

  stopPresence: async (): Promise<AirdropStatusResponse> => {
    const res = await apiClient.post<AirdropStatusResponse>('/airdrop/presence/stop')
    return res.data
  },

  watchAd: async (): Promise<AirdropStatusResponse> => {
    const res = await apiClient.post<AirdropStatusResponse>('/airdrop/watch-ad')
    return res.data
  },

  getLeaderboard: async (limit: number = 50): Promise<{ winners: AirdropLeaderboardEntry[] }> => {
    const res = await apiClient.get<{ winners: AirdropLeaderboardEntry[] }>(`/airdrop/leaderboard?limit=${limit}`)
    return res.data
  },

  getMonthlyLeaderboard: async (date?: string, limit: number = 50): Promise<{ winners: AirdropLeaderboardEntry[] }> => {
    const params = new URLSearchParams()
    if (date) params.set('date', date)
    params.set('limit', String(limit))
    const res = await apiClient.get<{ winners: AirdropLeaderboardEntry[] }>(`/airdrop/monthly-leaderboard?${params.toString()}`)
    return res.data
  },
}

export const rewardCodeApi = {
  listMyCodes: async (): Promise<{ codes: RewardCode[] }> => {
    const res = await apiClient.get<{ codes: RewardCode[] }>('/reward-codes')
    return res.data
  },

  unlock: async (code: string): Promise<{ code: RewardCode; prizes: GiftPrize[] }> => {
    const res = await apiClient.post<{ code: RewardCode; prizes: GiftPrize[] }>('/reward-codes/unlock', { code })
    return res.data
  },

  claim: async (code: string, prizeId: number): Promise<{ message: string; code: RewardCode; prize: GiftPrize }> => {
    const res = await apiClient.post<{ message: string; code: RewardCode; prize: GiftPrize }>('/reward-codes/claim', {
      code,
      prize_id: prizeId,
    })
    return res.data
  },

  useCode: async (code: string): Promise<{ message: string }> => {
    const res = await apiClient.post<{ message: string }>('/reward-codes/use', { code })
    return res.data
  },
}

export const walletApi = {
  getCoins: async (): Promise<{ coins: CoinMap; dollar: number }> => {
    const res = await apiClient.get<{ coins: CoinMap; dollar: number }>('/wallet')
    return res.data
  },

  getRates: async (): Promise<{ rates: CoinRateCents }> => {
    const res = await apiClient.get<{ rates: CoinRateCents }>('/wallet/rates')
    return res.data
  },

  convert: async (from: string, to: string, amount: number): Promise<{
    coins: CoinMap
    from: string
    to: string
    amount: number
    spent: number
    received: number
    leftover: number
  }> => {
    const res = await apiClient.post('/wallet/convert', { from, to, amount })
    return res.data
  },
}

export interface AdminAccountInfo {
  id: number
  username: string
  is_super: boolean
  is_active: boolean
  permissions: string[]
}

export const adminApi = {
  login: async (username: string, password: string): Promise<{ token: string; admin?: AdminAccountInfo }> => {
    const res = await apiClient.post<{ token: string; admin?: AdminAccountInfo }>('/auth/admin-login', { username, password })
    return res.data
  },

  getMe: async (): Promise<{ admin: AdminAccountInfo; all_permissions: string[]; permission_labels: Record<string, string> }> => {
    const res = await apiClient.get<{ admin: AdminAccountInfo; all_permissions: string[]; permission_labels: Record<string, string> }>('/admin/me', {
      headers: { 'X-Admin-Token': localStorage.getItem('admin_token') || '' },
    })
    return res.data
  },

  listAdmins: async (): Promise<{ admins: AdminAccountInfo[]; all_permissions: string[]; permission_labels: Record<string, string> }> => {
    const res = await apiClient.get<{ admins: AdminAccountInfo[]; all_permissions: string[]; permission_labels: Record<string, string> }>('/admin/admins', {
      headers: { 'X-Admin-Token': localStorage.getItem('admin_token') || '' },
    })
    return res.data
  },

  createAdmin: async (data: { username: string; password: string; permissions: string[] }): Promise<{ admin: AdminAccountInfo }> => {
    const res = await apiClient.post<{ admin: AdminAccountInfo }>('/admin/admins', data, {
      headers: { 'X-Admin-Token': localStorage.getItem('admin_token') || '' },
    })
    return res.data
  },

  updateAdmin: async (id: number, data: { username?: string; password?: string; permissions?: string[]; is_active?: boolean }): Promise<{ admin: AdminAccountInfo }> => {
    const res = await apiClient.patch<{ admin: AdminAccountInfo }>(`/admin/admins/${id}`, data, {
      headers: { 'X-Admin-Token': localStorage.getItem('admin_token') || '' },
    })
    return res.data
  },

  deleteAdmin: async (id: number): Promise<{ message: string }> => {
    const res = await apiClient.delete<{ message: string }>(`/admin/admins/${id}`, {
      headers: { 'X-Admin-Token': localStorage.getItem('admin_token') || '' },
    })
    return res.data
  },

  getRewardCodes: async (): Promise<{ codes: RewardCode[] }> => {
    const res = await apiClient.get<{ codes: RewardCode[] }>('/admin/reward-codes', {
      headers: { 'X-Admin-Token': localStorage.getItem('admin_token') || '' },
    })
    return res.data
  },

  getRewardCodesByDate: async (date: string): Promise<{ codes: RewardCode[] }> => {
    const res = await apiClient.get<{ codes: RewardCode[] }>(`/admin/reward-codes/date?date=${date}`, {
      headers: { 'X-Admin-Token': localStorage.getItem('admin_token') || '' },
    })
    return res.data
  },

  getRewardCodesByMonth: async (date: string): Promise<{ codes: RewardCode[] }> => {
    const res = await apiClient.get<{ codes: RewardCode[] }>(`/admin/reward-codes/month?date=${date}`, {
      headers: { 'X-Admin-Token': localStorage.getItem('admin_token') || '' },
    })
    return res.data
  },

  createRewardCode: async (data: { user_id?: number; award_type: 'daily' | 'monthly'; valid_hours?: number; code?: string }): Promise<{ code: RewardCode; message?: string }> => {
    const res = await apiClient.post<{ code: RewardCode; message?: string }>('/admin/reward-codes', data, {
      headers: { 'X-Admin-Token': localStorage.getItem('admin_token') || '' },
    })
    return res.data
  },

  getGiftPrizes: async (): Promise<{ prizes: GiftPrize[] }> => {
    const res = await apiClient.get<{ prizes: GiftPrize[] }>('/admin/gift-prizes', {
      headers: { 'X-Admin-Token': localStorage.getItem('admin_token') || '' },
    })
    return res.data
  },

  createGiftPrize: async (data: { title: string; description?: string; image_url?: string; sort_order?: number }): Promise<{ prize: GiftPrize; message?: string }> => {
    const res = await apiClient.post<{ prize: GiftPrize; message?: string }>('/admin/gift-prizes', data, {
      headers: { 'X-Admin-Token': localStorage.getItem('admin_token') || '' },
    })
    return res.data
  },

  updateGiftPrize: async (id: number, data: { title: string; description?: string; image_url?: string; is_active: boolean; sort_order?: number }): Promise<{ prize: GiftPrize; message?: string }> => {
    const res = await apiClient.put<{ prize: GiftPrize; message?: string }>(`/admin/gift-prizes/${id}`, data, {
      headers: { 'X-Admin-Token': localStorage.getItem('admin_token') || '' },
    })
    return res.data
  },

  deleteGiftPrize: async (id: number): Promise<{ message: string }> => {
    const res = await apiClient.delete<{ message: string }>(`/admin/gift-prizes/${id}`, {
      headers: { 'X-Admin-Token': localStorage.getItem('admin_token') || '' },
    })
    return res.data
  },

  getGiftWinners: async (): Promise<{ winners: RewardCode[] }> => {
    const res = await apiClient.get<{ winners: RewardCode[] }>('/admin/gift-winners', {
      headers: { 'X-Admin-Token': localStorage.getItem('admin_token') || '' },
    })
    return res.data
  },

  getDailyScores: async (date: string): Promise<{ scores: AirdropLeaderboardEntry[] }> => {
    const res = await apiClient.get<{ scores: AirdropLeaderboardEntry[] }>(`/admin/airdrop/scores?date=${date}`, {
      headers: { 'X-Admin-Token': localStorage.getItem('admin_token') || '' },
    })
    return res.data
  },

  getMonthlyScores: async (date: string): Promise<{ scores: AirdropLeaderboardEntry[] }> => {
    const res = await apiClient.get<{ scores: AirdropLeaderboardEntry[] }>(`/admin/airdrop/monthly-scores?date=${date}`, {
      headers: { 'X-Admin-Token': localStorage.getItem('admin_token') || '' },
    })
    return res.data
  },

  // Football clubs (name + flag catalog). limit=0 = full catalog (no server cap).
  getFootballClubs: async (activeOnly = false): Promise<{ clubs: FootballClub[]; total: number }> => {
    const params = new URLSearchParams({ limit: '0' })
    if (activeOnly) params.set('active', '1')
    const res = await apiClient.get<{ clubs: FootballClub[]; total: number }>(`/admin/football/clubs?${params}`, {
      headers: { 'X-Admin-Token': localStorage.getItem('admin_token') || '' },
    })
    return res.data
  },

  createFootballClub: async (data: { name: string; flag_url?: string }): Promise<{ club: FootballClub }> => {
    const res = await apiClient.post<{ club: FootballClub }>('/admin/football/clubs', data, {
      headers: { 'X-Admin-Token': localStorage.getItem('admin_token') || '' },
    })
    return res.data
  },

  updateFootballClub: async (id: number, data: { name?: string; flag_url?: string; is_active?: boolean }): Promise<{ club: FootballClub }> => {
    const res = await apiClient.patch<{ club: FootballClub }>(`/admin/football/clubs/${id}`, data, {
      headers: { 'X-Admin-Token': localStorage.getItem('admin_token') || '' },
    })
    return res.data
  },

  deleteFootballClub: async (id: number): Promise<{ message: string }> => {
    const res = await apiClient.delete<{ message: string }>(`/admin/football/clubs/${id}`, {
      headers: { 'X-Admin-Token': localStorage.getItem('admin_token') || '' },
    })
    return res.data
  },

  // Football match admin
  createFootballMatch: async (data: {
    home_team?: string
    away_team?: string
    home_team_id?: number
    away_team_id?: number
    start_time: number
    mode?: FootballGameMode
  }): Promise<{ match: FootballMatch }> => {
    const res = await apiClient.post<{ match: FootballMatch }>('/admin/football/matches', data, {
      headers: { 'X-Admin-Token': localStorage.getItem('admin_token') || '' },
    })
    return res.data
  },

  processMatchResult: async (matchId: number, homeScore: number, awayScore: number): Promise<{ message: string }> => {
    const res = await apiClient.post<{ message: string }>(`/admin/football/matches/${matchId}/result`, { home_score: homeScore, away_score: awayScore }, {
      headers: { 'X-Admin-Token': localStorage.getItem('admin_token') || '' },
    })
    return res.data
  },

  getRaffleWinners: async (matchId: number): Promise<{ winners: MatchPrediction[] }> => {
    const res = await apiClient.get<{ winners: MatchPrediction[] }>(`/admin/football/matches/${matchId}/winners`, {
      headers: { 'X-Admin-Token': localStorage.getItem('admin_token') || '' },
    })
    return res.data
  },

  getAllMatches: async (limit: number = 20, offset: number = 0, mode?: FootballGameMode): Promise<{ matches: FootballMatch[] }> => {
    const modeParam = mode ? `&mode=${mode}` : ''
    const res = await apiClient.get<{ matches: FootballMatch[] }>(`/admin/football/matches?limit=${limit}&offset=${offset}${modeParam}`, {
      headers: { 'X-Admin-Token': localStorage.getItem('admin_token') || '' },
    })
    return res.data
  },

  // Football league admin
  getLeagues: async (limit: number = 50, offset: number = 0): Promise<{ leagues: FootballLeague[] }> => {
    const res = await apiClient.get<{ leagues: FootballLeague[] }>(`/admin/football/league?limit=${limit}&offset=${offset}`, {
      headers: { 'X-Admin-Token': localStorage.getItem('admin_token') || '' },
    })
    return res.data
  },

  createLeague: async (data: {
    name: string
    mode: FootballGameMode
    description?: string
    start_date: number
    end_date: number
  }): Promise<{ league: FootballLeague }> => {
    const res = await apiClient.post<{ league: FootballLeague }>('/admin/football/league', data, {
      headers: { 'X-Admin-Token': localStorage.getItem('admin_token') || '' },
    })
    return res.data
  },

  updateLeague: async (leagueId: number, data: { name: string; description?: string; start_date?: number; end_date?: number; is_active?: boolean }): Promise<{ league: FootballLeague }> => {
    const res = await apiClient.put<{ league: FootballLeague }>(`/admin/football/league/${leagueId}`, data, {
      headers: { 'X-Admin-Token': localStorage.getItem('admin_token') || '' },
    })
    return res.data
  },

  deleteLeague: async (leagueId: number): Promise<{ message: string }> => {
    const res = await apiClient.delete<{ message: string }>(`/admin/football/league/${leagueId}`, {
      headers: { 'X-Admin-Token': localStorage.getItem('admin_token') || '' },
    })
    return res.data
  },

  processLeagueMatchResult: async (
    matchId: number,
    homeScore: number,
    awayScore: number,
    raffleWinnerCount?: number,
  ): Promise<{ message: string }> => {
    const body: { home_score: number; away_score: number; raffle_winner_count?: number } = {
      home_score: homeScore,
      away_score: awayScore,
    }
    if (typeof raffleWinnerCount === 'number' && raffleWinnerCount > 0) {
      body.raffle_winner_count = raffleWinnerCount
    }
    const res = await apiClient.post<{ message: string }>(
      `/admin/football/league/${matchId}/process-result`,
      body,
      { headers: { 'X-Admin-Token': localStorage.getItem('admin_token') || '' } },
    )
    return res.data
  },

  processDreamTeamResults: async (matchId: number): Promise<{ message: string }> => {
    const res = await apiClient.post<{ message: string }>(`/admin/football/league/${matchId}/process-dream-teams`, {}, {
      headers: { 'X-Admin-Token': localStorage.getItem('admin_token') || '' },
    })
    return res.data
  },

  getLeagueRaffleWinners: async (matchId: number): Promise<{ winners: RaffleWinner[] }> => {
    const res = await apiClient.get<{ winners: RaffleWinner[] }>(`/admin/football/league/${matchId}/winnings`, {
      headers: { 'X-Admin-Token': localStorage.getItem('admin_token') || '' },
    })
    return res.data
  },

  setMatchPlayerStats: async (matchId: number, stats: MatchPlayerStat[]): Promise<{ message: string }> => {
    const res = await apiClient.put<{ message: string }>(`/admin/football/matches/${matchId}/player-stats`, { stats }, {
      headers: { 'X-Admin-Token': localStorage.getItem('admin_token') || '' },
    })
    return res.data
  },

  getMatchPlayerStats: async (matchId: number): Promise<{ stats: MatchPlayerStat[] }> => {
    const res = await apiClient.get<{ stats: MatchPlayerStat[] }>(`/admin/football/matches/${matchId}/player-stats`, {
      headers: { 'X-Admin-Token': localStorage.getItem('admin_token') || '' },
    })
    return res.data
  },

  // Upload player photo
  uploadPlayerPhoto: async (file: File): Promise<{ url: string }> => {
    const formData = new FormData()
    formData.append('photo', file)
    const res = await apiClient.post<{ url: string }>('/admin/upload', formData, {
      headers: {
        'X-Admin-Token': localStorage.getItem('admin_token') || '',
      },
    })
    return res.data
  },

  // Fantasy players admin
  createFantasyPlayer: async (data: { name: string; position: string; team: string; photo_url?: string }): Promise<{ player: FantasyPlayer }> => {
    const res = await apiClient.post<{ player: FantasyPlayer }>('/admin/fantasy/players', data, {
      headers: { 'X-Admin-Token': localStorage.getItem('admin_token') || '' },
    })
    return res.data
  },

  updateFantasyPlayer: async (id: number, data: { name: string; position: string; team: string; photo_url?: string }): Promise<{ message: string }> => {
    const res = await apiClient.patch<{ message: string }>(`/admin/fantasy/players/${id}`, data, {
      headers: { 'X-Admin-Token': localStorage.getItem('admin_token') || '' },
    })
    return res.data
  },

  deleteFantasyPlayer: async (id: number): Promise<{ message: string }> => {
    const res = await apiClient.delete<{ message: string }>(`/admin/fantasy/players/${id}`, {
      headers: { 'X-Admin-Token': localStorage.getItem('admin_token') || '' },
    })
    return res.data
  },

  // Transfers admin
  getPendingTransfers: async (limit: number = 20, offset: number = 0): Promise<{ transfers: CoinTransfer[] }> => {
    const res = await apiClient.get<{ transfers: CoinTransfer[] }>(`/admin/transfers/pending?limit=${limit}&offset=${offset}`, {
      headers: { 'X-Admin-Token': localStorage.getItem('admin_token') || '' },
    })
    return res.data
  },

  processTransfer: async (id: number, approve: boolean, note: string): Promise<{ message: string }> => {
    const res = await apiClient.post<{ message: string }>(`/admin/transfers/${id}/process`, { approve, note }, {
      headers: { 'X-Admin-Token': localStorage.getItem('admin_token') || '' },
    })
    return res.data
  },

  // Guess number admin
  getAllGuessWinners: async (limit: number = 100): Promise<{ winners: GameRecord[] }> => {
    const res = await apiClient.get<{ winners: GameRecord[] }>(`/admin/guess-number/winners?limit=${limit}`, {
      headers: { 'X-Admin-Token': localStorage.getItem('admin_token') || '' },
    })
    return res.data
  },

  generateGuessNumber: async (): Promise<{ number?: string; message?: string; daily_number?: { number: string } }> => {
    const res = await apiClient.post<{ number?: string; message?: string; daily_number?: { number: string } }>('/admin/guess-number/generate', {}, {
      headers: { 'X-Admin-Token': localStorage.getItem('admin_token') || '' },
    })
    return res.data
  },

  setGuessNumber: async (number: string): Promise<{ message: string }> => {
    const res = await apiClient.post<{ message: string }>('/admin/guess-number/number', { number }, {
      headers: { 'X-Admin-Token': localStorage.getItem('admin_token') || '' },
    })
    return res.data
  },

  revealGuessNumber: async (): Promise<{ message: string }> => {
    const res = await apiClient.post<{ message: string }>('/admin/guess-number/reveal', {}, {
      headers: { 'X-Admin-Token': localStorage.getItem('admin_token') || '' },
    })
    return res.data
  },

  getGuessNumberStatus: async (): Promise<GuessNumberStatus> => {
    const res = await apiClient.get<GuessNumberStatus>('/admin/guess-number/status', {
      headers: { 'X-Admin-Token': localStorage.getItem('admin_token') || '' },
    })
    return res.data
  },

  getMorseAdminStatus: async (): Promise<{
    date: string
    words: string[]
    status: string
    solves_today: number
    window_open: boolean
  }> => {
    const res = await apiClient.get('/admin/morse/status', {
      headers: { 'X-Admin-Token': localStorage.getItem('admin_token') || '' },
    })
    return res.data
  },

  getCoinRates: async (): Promise<{ rates: CoinRateCents; exchanges: CoinExchangeRow[] }> => {
    const res = await apiClient.get<{ rates: CoinRateCents; exchanges: CoinExchangeRow[] }>('/admin/coin-rates', {
      headers: { 'X-Admin-Token': localStorage.getItem('admin_token') || '' },
    })
    return res.data
  },

  saveCoinRates: async (rates: Record<string, string>): Promise<{ rates: CoinRateCents }> => {
    const res = await apiClient.put<{ rates: CoinRateCents }>('/admin/coin-rates', rates, {
      headers: { 'X-Admin-Token': localStorage.getItem('admin_token') || '' },
    })
    return res.data
  },

  setMorseWords: async (words: string[]): Promise<{ message: string }> => {
    const res = await apiClient.post<{ message: string }>('/admin/morse/words', { words }, {
      headers: { 'X-Admin-Token': localStorage.getItem('admin_token') || '' },
    })
    return res.data
  },

  // Dashboard stats
  getDashboardStats: async (): Promise<{ stats: AdminStats }> => {
    const res = await apiClient.get<{ stats: AdminStats }>('/admin/stats/dashboard', {
      headers: { 'X-Admin-Token': localStorage.getItem('admin_token') || '' },
    })
    return res.data
  },

  /** Download PostgreSQL dump as .sql.gz */
  downloadDatabaseBackup: async (): Promise<{ blob: Blob; filename: string }> => {
    return adminApi.downloadBackupFile('/admin/backup/database', 'mini_app_db.sql.gz')
  },

  /** Download full backup: database.sql.gz + uploads/ as .tar.gz */
  downloadFullBackup: async (): Promise<{ blob: Blob; filename: string }> => {
    return adminApi.downloadBackupFile('/admin/backup/full', 'mini_app_full.tar.gz')
  },

  downloadBackupFile: async (path: string, fallbackName: string): Promise<{ blob: Blob; filename: string }> => {
    const res = await apiClient.get<Blob>(path, {
      headers: { 'X-Admin-Token': localStorage.getItem('admin_token') || '' },
      responseType: 'blob',
      timeout: 600000,
    })
    const disposition = String(res.headers['content-disposition'] || '')
    const match = /filename="?([^";]+)"?/i.exec(disposition)
    const filename = match?.[1] || fallbackName
    return { blob: res.data, filename }
  },

  getUsers: async (limit: number = 20, offset: number = 0): Promise<{ users: User[]; total: number }> => {
    const res = await apiClient.get<{ users: User[]; total: number }>(`/admin/stats/users?limit=${limit}&offset=${offset}`, {
      headers: { 'X-Admin-Token': localStorage.getItem('admin_token') || '' },
    })
    return res.data
  },

  getUserDetail: async (userId: number): Promise<AdminUserDetail> => {
    const res = await apiClient.get<AdminUserDetail>(`/admin/stats/users/${userId}`, {
      headers: { 'X-Admin-Token': localStorage.getItem('admin_token') || '' },
    })
    return res.data
  },

  getFantasyPlayers: async (): Promise<{ players: FantasyPlayer[] }> => {
    const res = await apiClient.get<{ players: FantasyPlayer[] }>('/admin/fantasy/players', {
      headers: { 'X-Admin-Token': localStorage.getItem('admin_token') || '' },
    })
    return res.data
  },

  // Player groups admin
  getPlayerGroups: async (limit = 20, offset = 0): Promise<{ groups: PlayerGroup[] }> => {
    const res = await apiClient.get<{ groups: PlayerGroup[] }>(`/admin/fantasy/groups?limit=${limit}&offset=${offset}`, {
      headers: { 'X-Admin-Token': localStorage.getItem('admin_token') || '' },
    })
    return res.data
  },

  createPlayerGroup: async (data: { title: string; max_cards: number }): Promise<{ group: PlayerGroup }> => {
    const res = await apiClient.post<{ group: PlayerGroup }>('/admin/fantasy/groups', data, {
      headers: { 'X-Admin-Token': localStorage.getItem('admin_token') || '' },
    })
    return res.data
  },

  updatePlayerGroup: async (id: number, data: { title: string; max_cards: number; is_active: boolean }): Promise<{ group: PlayerGroup }> => {
    const res = await apiClient.patch<{ group: PlayerGroup }>(`/admin/fantasy/groups/${id}`, data, {
      headers: { 'X-Admin-Token': localStorage.getItem('admin_token') || '' },
    })
    return res.data
  },

  deletePlayerGroup: async (id: number): Promise<{ message: string }> => {
    const res = await apiClient.delete<{ message: string }>(`/admin/fantasy/groups/${id}`, {
      headers: { 'X-Admin-Token': localStorage.getItem('admin_token') || '' },
    })
    return res.data
  },

  getGroupPlayers: async (groupId: number): Promise<{ players: FantasyPlayer[] }> => {
    const res = await apiClient.get<{ players: FantasyPlayer[] }>(`/admin/fantasy/groups/${groupId}/players`, {
      headers: { 'X-Admin-Token': localStorage.getItem('admin_token') || '' },
    })
    return res.data
  },

  addPlayerToGroup: async (groupId: number, playerId: number): Promise<{ message: string }> => {
    const res = await apiClient.post<{ message: string }>(`/admin/fantasy/groups/${groupId}/players/${playerId}`, {}, {
      headers: { 'X-Admin-Token': localStorage.getItem('admin_token') || '' },
    })
    return res.data
  },

  removePlayerFromGroup: async (groupId: number, playerId: number): Promise<{ message: string }> => {
    const res = await apiClient.delete<{ message: string }>(`/admin/fantasy/groups/${groupId}/players/${playerId}`, {
      headers: { 'X-Admin-Token': localStorage.getItem('admin_token') || '' },
    })
    return res.data
  },

  getGroupPlayerCount: async (groupId: number): Promise<{ count: number }> => {
    const res = await apiClient.get<{ count: number }>(`/admin/fantasy/groups/${groupId}/player-count`, {
      headers: { 'X-Admin-Token': localStorage.getItem('admin_token') || '' },
    })
    return res.data
  },

  // Match <-> player groups
  getMatchGroups: async (matchId: number): Promise<{ groups: PlayerGroup[] }> => {
    const res = await apiClient.get<{ groups: PlayerGroup[] }>(`/admin/football/matches/${matchId}/groups`, {
      headers: { 'X-Admin-Token': localStorage.getItem('admin_token') || '' },
    })
    return res.data
  },

  setMatchGroups: async (matchId: number, groupIds: number[]): Promise<{ message: string }> => {
    const res = await apiClient.put<{ message: string }>(`/admin/football/matches/${matchId}/groups`, {
      group_ids: groupIds,
    }, {
      headers: { 'X-Admin-Token': localStorage.getItem('admin_token') || '' },
    })
    return res.data
  },

  getAllTransfers: async (limit: number = 50, offset: number = 0): Promise<{ transfers: CoinTransfer[] }> => {
    const res = await apiClient.get<{ transfers: CoinTransfer[] }>(`/admin/transfers?limit=${limit}&offset=${offset}`, {
      headers: { 'X-Admin-Token': localStorage.getItem('admin_token') || '' },
    })
    return res.data
  },

  getMessages: async (limit: number = 50, offset: number = 0): Promise<{ messages: AdminMessage[] }> => {
    const res = await apiClient.get<{ messages: AdminMessage[] }>(`/admin/messages?limit=${limit}&offset=${offset}`, {
      headers: { 'X-Admin-Token': localStorage.getItem('admin_token') || '' },
    })
    return res.data
  },

  sendMessage: async (userId: number, text: string): Promise<{ message: AdminMessage }> => {
    const res = await apiClient.post<{ message: AdminMessage }>('/admin/messages', { user_id: userId, text }, {
      headers: { 'X-Admin-Token': localStorage.getItem('admin_token') || '' },
    })
    return res.data
  },

  listSupportTickets: async (status?: 'open' | 'closed', limit = 30, offset = 0): Promise<{ tickets: SupportTicket[] }> => {
    const params = new URLSearchParams({ limit: String(limit), offset: String(offset) })
    if (status) params.set('status', status)
    const res = await apiClient.get<{ tickets: SupportTicket[] }>(`/admin/support/tickets?${params}`, {
      headers: { 'X-Admin-Token': localStorage.getItem('admin_token') || '' },
    })
    return res.data
  },

  getSupportTicket: async (id: number): Promise<{ ticket: SupportTicket }> => {
    const res = await apiClient.get<{ ticket: SupportTicket }>(`/admin/support/tickets/${id}`, {
      headers: { 'X-Admin-Token': localStorage.getItem('admin_token') || '' },
    })
    return res.data
  },

  replySupportTicket: async (id: number, text: string): Promise<{ message: SupportMessage }> => {
    const res = await apiClient.post<{ message: SupportMessage }>(`/admin/support/tickets/${id}/messages`, { text }, {
      headers: { 'X-Admin-Token': localStorage.getItem('admin_token') || '' },
    })
    return res.data
  },

  setSupportTicketStatus: async (id: number, status: 'open' | 'closed'): Promise<{ ticket: SupportTicket }> => {
    const res = await apiClient.post<{ ticket: SupportTicket }>(`/admin/support/tickets/${id}/status`, { status }, {
      headers: { 'X-Admin-Token': localStorage.getItem('admin_token') || '' },
    })
    return res.data
  },
}

export const supportApi = {
  getChat: async (): Promise<{ ticket: SupportTicket }> => {
    const res = await apiClient.get<{ ticket: SupportTicket }>('/support/chat')
    return res.data
  },

  sendMessage: async (text: string): Promise<{ ticket: SupportTicket; message: SupportMessage }> => {
    const res = await apiClient.post<{ ticket: SupportTicket; message: SupportMessage }>('/support/chat/messages', { text })
    return res.data
  },
}

export const footballApi = {
  getUpcomingMatches: async (limit: number = 20, mode?: FootballGameMode): Promise<{ matches: FootballMatch[] }> => {
    const modeParam = mode ? `&mode=${mode}` : ''
    const res = await apiClient.get<{ matches: FootballMatch[] }>(`/football/matches?limit=${limit}${modeParam}`)
    return res.data
  },

  getAllMatches: async (limit: number = 20, offset: number = 0): Promise<{ matches: FootballMatch[] }> => {
    const res = await apiClient.get<{ matches: FootballMatch[] }>(`/football/matches/all?limit=${limit}&offset=${offset}`)
    return res.data
  },

  getMatch: async (id: number): Promise<{ match: FootballMatch }> => {
    const res = await apiClient.get<{ match: FootballMatch }>(`/football/matches/${id}`)
    return res.data
  },

  submitPrediction: async (matchId: number, choice: 'home' | 'away' | 'draw'): Promise<{ prediction: MatchPrediction }> => {
    const res = await apiClient.post<{ prediction: MatchPrediction }>('/football/predict', { match_id: matchId, choice })
    return res.data
  },

  getMyPredictions: async (): Promise<{ predictions: MatchPrediction[] }> => {
    const res = await apiClient.get<{ predictions: MatchPrediction[] }>('/football/my-predictions')
    return res.data
  },

  getMatchPredictions: async (matchId: number): Promise<{ predictions: MatchPrediction[] }> => {
    const res = await apiClient.get<{ predictions: MatchPrediction[] }>(`/football/matches/${matchId}/predictions`)
    return res.data
  },

  // League
  getActiveLeague: async (mode: FootballGameMode): Promise<{ league: FootballLeague }> => {
    const res = await apiClient.get<{ league: FootballLeague }>(`/football/league/active?mode=${mode}`)
    return res.data
  },

  getUserScore: async (id: number): Promise<{ score: UserFootballScore }> => {
    const res = await apiClient.get<{ score: UserFootballScore }>(`/football/league/${id}/score`)
    return res.data
  },

  getLeaderboard: async (id: number, limit: number = 10): Promise<{ leaderboard: UserFootballScore[] }> => {
    const res = await apiClient.get<{ leaderboard: UserFootballScore[] }>(`/football/league/${id}/leaderboard?limit=${limit}`)
    return res.data
  },

  getMonthlyLeaderboard: async (id: number, limit: number = 10): Promise<{ leaderboard: UserFootballScore[] }> => {
    const res = await apiClient.get<{ leaderboard: UserFootballScore[] }>(`/football/league/${id}/monthly-leaderboard?limit=${limit}`)
    return res.data
  },

  getUserWinnings: async (id: number): Promise<{ winnings: RaffleWinner[] }> => {
    const res = await apiClient.get<{ winnings: RaffleWinner[] }>(`/football/league/${id}/winnings`)
    return res.data
  },

  submitLeaguePrediction: async (
    matchId: number,
    choices: Array<'home' | 'away' | 'draw'>,
  ): Promise<{ prediction: MatchPrediction }> => {
    const res = await apiClient.post<{ prediction: MatchPrediction }>('/football/league/predict', {
      match_id: matchId,
      choices,
      choice: choices[0],
    })
    return res.data
  },

  submitLeaguePredictionsBatch: async (
    predictions: Array<{ match_id: number; choices: Array<'home' | 'away' | 'draw'> }>,
  ): Promise<{ predictions: MatchPrediction[] }> => {
    const res = await apiClient.post<{ predictions: MatchPrediction[] }>(
      '/football/league/predict-batch',
      { predictions },
    )
    return res.data
  },

  getDayGoalsStatus: async (leagueId: number): Promise<LeagueDayGoalsStatus> => {
    const res = await apiClient.get<LeagueDayGoalsStatus>(`/football/league/${leagueId}/day-goals`)
    return res.data
  },

  submitDayGoals: async (
    leagueId: number,
    body: {
      match_date: string
      predicted_home_total: number
      predicted_away_total: number
      payment: 'bronze' | 'ads'
    },
  ): Promise<{ prediction: LeagueDayGoalsPrediction }> => {
    const res = await apiClient.post<{ prediction: LeagueDayGoalsPrediction }>(
      `/football/league/${leagueId}/day-goals`,
      body,
    )
    return res.data
  },

  getMyLeaguePredictions: async (): Promise<{ predictions: MatchPrediction[] }> => {
    const res = await apiClient.get<{ predictions: MatchPrediction[] }>('/football/league/my-predictions')
    return res.data
  },

  getMyScores: async (): Promise<{ scores: UserLeagueScore[] }> => {
    const res = await apiClient.get<{ scores: UserLeagueScore[] }>('/football/league/my-scores')
    return res.data
  },

  getMyWinnings: async (): Promise<{ winnings: RaffleWinner[] }> => {
    const res = await apiClient.get<{ winnings: RaffleWinner[] }>('/football/league/my-winnings')
    return res.data
  },

  createDreamTeam: async (data: DreamTeamCreateDTO): Promise<{ dream_team: DreamTeam }> => {
    const res = await apiClient.post<{ dream_team: DreamTeam }>('/football/league/dream-team', data)
    return res.data
  },

  getDreamTeam: async (matchId: number): Promise<{ dream_team: DreamTeam | null }> => {
    try {
      const res = await apiClient.get<{ dream_team: DreamTeam }>(`/football/league/dream-team/${matchId}`)
      return res.data
    } catch (error: any) {
      if (error.response?.status === 404) return { dream_team: null }
      throw error
    }
  },

  getDreamTeamById: async (id: number): Promise<{ dream_team: DreamTeam; voting_open?: boolean }> => {
    const res = await apiClient.get<{ dream_team: DreamTeam; voting_open?: boolean }>(
      `/football/league/dream-team/by-id/${id}`,
    )
    return res.data
  },

  voteDreamTeam: async (teamId: number, rating: number): Promise<{ message: string }> => {
    const res = await apiClient.post<{ message: string }>('/football/league/dream-teams/vote', {
      team_id: teamId,
      rating,
    })
    return res.data
  },

  getMyDreamTeamVote: async (teamId: number): Promise<{ vote: DreamTeamVote | null }> => {
    const res = await apiClient.get<{ vote: DreamTeamVote | null }>(
      `/football/league/dream-teams/my-vote?team_id=${teamId}`,
    )
    return res.data
  },

  getMatchDreamTeams: async (matchId: number): Promise<{ dream_teams: DreamTeam[] }> => {
    const res = await apiClient.get<{ dream_teams: DreamTeam[] }>(`/football/league/matches/${matchId}/dream-teams`)
    return res.data
  },

  updateDreamTeam: async (teamId: number, data: DreamTeamCreateDTO): Promise<{ dream_team: DreamTeam }> => {
    const res = await apiClient.put<{ dream_team: DreamTeam }>(`/football/league/dream-team/${teamId}`, data)
    return res.data
  },

  submitDreamTeam: async (teamId: number): Promise<{ message: string }> => {
    const res = await apiClient.post<{ message: string }>(`/football/league/dream-team/${teamId}/submit`)
    return res.data
  },

  exploreDreamTeams: async (
    query: string,
    limit: number = 30,
    offset: number = 0,
  ): Promise<{ teams: DreamTeamExploreItem[] }> => {
    const params = new URLSearchParams({
      query,
      limit: String(limit),
      offset: String(offset),
    })
    const res = await apiClient.get<{ teams: DreamTeamExploreItem[] }>(
      `/football/league/dream-teams/explore?${params.toString()}`,
    )
    return res.data
  },
}

export const fantasyApi = {
  getAllPlayers: async (): Promise<{ players: FantasyPlayer[] }> => {
    const res = await apiClient.get<{ players: FantasyPlayer[] }>('/fantasy/players')
    return res.data
  },

  getPlayersByPosition: async (position: string): Promise<{ players: FantasyPlayer[] }> => {
    const res = await apiClient.get<{ players: FantasyPlayer[] }>(`/fantasy/players/position?position=${position}`)
    return res.data
  },

  getMatchPlayers: async (matchId: number): Promise<{ players: FantasyPlayer[]; pool: MatchFantasyPool }> => {
    const res = await apiClient.get<{ players: FantasyPlayer[]; pool: MatchFantasyPool }>(`/fantasy/matches/${matchId}/players`)
    return res.data
  },

  getFormations: async (): Promise<{ formations: string[] }> => {
    const res = await apiClient.get<{ formations: string[] }>('/fantasy/formations')
    return res.data
  },

  createTeam: async (data: FantasyTeamCreateDTO): Promise<{ team: FantasyTeam }> => {
    const res = await apiClient.post<{ team: FantasyTeam }>('/fantasy/teams', data)
    return res.data
  },

  updateTeam: async (teamId: number, data: FantasyTeamCreateDTO): Promise<{ team: FantasyTeam }> => {
    const res = await apiClient.put<{ team: FantasyTeam }>(`/fantasy/teams/${teamId}`, data)
    return res.data
  },

  getTeam: async (id: number): Promise<{ team: FantasyTeam }> => {
    const res = await apiClient.get<{ team: FantasyTeam }>(`/fantasy/teams/${id}`)
    return res.data
  },

  getTeamByShareToken: async (token: string): Promise<{ team: FantasyTeam; match: MatchVotingInfo }> => {
    const res = await apiClient.get<{ team: FantasyTeam; match: MatchVotingInfo }>(`/fantasy/teams/share?token=${token}`)
    return res.data
  },

  getTeamWithPlayers: async (id: number): Promise<{ team: FantasyTeam; players: FantasyTeamPlayer[] }> => {
    const res = await apiClient.get<{ team: FantasyTeam; players: FantasyTeamPlayer[] }>(`/fantasy/teams/${id}/players`)
    return res.data
  },

  getMyTeamForMatch: async (matchId: number): Promise<{ team: FantasyTeam | null }> => {
    const res = await apiClient.get<{ team: FantasyTeam | null }>(`/fantasy/my-team?match_id=${matchId}`)
    return res.data
  },

  getMatchTeams: async (matchId: number): Promise<{ teams: FantasyTeam[] }> => {
    const res = await apiClient.get<{ teams: FantasyTeam[] }>(`/fantasy/match-teams?match_id=${matchId}`)
    return res.data
  },

  submitTeam: async (teamId: number): Promise<{ message: string }> => {
    const res = await apiClient.post<{ message: string }>(`/fantasy/teams/${teamId}/submit`)
    return res.data
  },

  vote: async (teamId: number, rating: number): Promise<{ message: string }> => {
    const res = await apiClient.post<{ message: string }>('/fantasy/vote', { team_id: teamId, rating })
    return res.data
  },

  getTopTeams: async (matchId: number, limit: number = 5): Promise<{ teams: FantasyTeam[] }> => {
    const res = await apiClient.get<{ teams: FantasyTeam[] }>(`/fantasy/top-teams?match_id=${matchId}&limit=${limit}`)
    return res.data
  },

  getMyVote: async (teamId: number): Promise<{ vote: TeamVote | null }> => {
    const res = await apiClient.get<{ vote: TeamVote | null }>(`/fantasy/my-vote?team_id=${teamId}`)
    return res.data
  },

  exploreTeams: async (query: string, limit: number = 30, offset: number = 0): Promise<{ teams: TeamExploreItem[] }> => {
    const params = new URLSearchParams({ limit: String(limit), offset: String(offset) })
    if (query.trim()) params.set('query', query.trim())
    const res = await apiClient.get<{ teams: TeamExploreItem[] }>(`/fantasy/explore?${params.toString()}`)
    return res.data
  },
}

export const transferApi = {
  setPIN: async (pin: string): Promise<{ message: string }> => {
    const res = await apiClient.post<{ message: string }>('/transfers/pin', { pin })
    return res.data
  },

  verifyPIN: async (pin: string): Promise<{ message: string }> => {
    const res = await apiClient.post<{ message: string }>('/transfers/pin/verify', { pin })
    return res.data
  },

  hasPIN: async (): Promise<{ has_pin: boolean }> => {
    const res = await apiClient.get<{ has_pin: boolean }>('/transfers/pin/status')
    return res.data
  },

  transferToUser: async (recipient: string, coinType: 'bronze' | 'silver' | 'gold' | 'diamond', amount: number, pin: string): Promise<{ message: string }> => {
    const res = await apiClient.post<{ message: string }>('/transfers/to-user', { recipient, coin_type: coinType, amount, pin })
    return res.data
  },

  requestAdminTransfer: async (coinType: 'bronze' | 'silver' | 'gold' | 'diamond', amount: number, walletAddress: string, pin: string): Promise<{ message: string }> => {
    const res = await apiClient.post<{ message: string }>('/transfers/to-admin', { coin_type: coinType, amount, wallet_address: walletAddress, pin })
    return res.data
  },

  getTransfers: async (limit: number = 20, offset: number = 0): Promise<{ transfers: CoinTransfer[] }> => {
    const res = await apiClient.get<{ transfers: CoinTransfer[] }>(`/transfers?limit=${limit}&offset=${offset}`)
    return res.data
  },
}

const adminHeaders = () => ({ 'X-Admin-Token': localStorage.getItem('admin_token') || '' })

export const horseApi = {
  listCourses: async (limit = 50, offset = 0): Promise<{ courses: HorseCourse[] }> => {
    const res = await apiClient.get<{ courses: HorseCourse[] }>(`/horse/courses?limit=${limit}&offset=${offset}`)
    return res.data
  },
  getCourse: async (id: number): Promise<{ course: HorseCourse }> => {
    const res = await apiClient.get<{ course: HorseCourse }>(`/horse/courses/${id}`)
    return res.data
  },
  listRounds: async (mode?: HorsePollMode, openOnly = true): Promise<{ rounds: HorsePollRound[] }> => {
    const params = new URLSearchParams()
    if (mode) params.set('mode', mode)
    if (openOnly) params.set('open', '1')
    const res = await apiClient.get<{ rounds: HorsePollRound[] }>(`/horse/rounds?${params}`)
    return res.data
  },
  getRound: async (id: number): Promise<{ round: HorsePollRound }> => {
    const res = await apiClient.get<{ round: HorsePollRound }>(`/horse/rounds/${id}`)
    return res.data
  },
  submit: async (
    roundId: number,
    selections: Record<string, number[]>,
    payment?: HorseEntryPayment,
  ): Promise<{ entry: HorsePollEntry }> => {
    const res = await apiClient.post<{ entry: HorsePollEntry }>(`/horse/rounds/${roundId}/submit`, {
      selections,
      payment: payment || 'bronze',
    })
    return res.data
  },
  entryPayment: async (roundId: number): Promise<HorseEntryPaymentStatus> => {
    const res = await apiClient.get<HorseEntryPaymentStatus>(`/horse/rounds/${roundId}/entry-payment`)
    return res.data
  },
  watchAd: async (roundId: number): Promise<HorseEntryPaymentStatus> => {
    const res = await apiClient.post<HorseEntryPaymentStatus>(`/horse/rounds/${roundId}/watch-ad`)
    return res.data
  },
  myHistory: async (limit = 30): Promise<{ items: HorseHistoryItem[] }> => {
    const res = await apiClient.get<{ items: HorseHistoryItem[] }>(`/horse/history?limit=${limit}`)
    return res.data
  },
  myEntry: async (roundId: number): Promise<{ entry: HorsePollEntry | null }> => {
    const res = await apiClient.get<{ entry: HorsePollEntry | null }>(`/horse/rounds/${roundId}/my-entry`)
    return res.data
  },
  roundLeaderboard: async (roundId: number, limit = 20): Promise<{ entries: HorsePollEntry[] }> => {
    const res = await apiClient.get<{ entries: HorsePollEntry[] }>(`/horse/rounds/${roundId}/leaderboard?limit=${limit}`)
    return res.data
  },
  leagueLeaderboard: async (month?: string, limit = 20): Promise<{ leaderboard: UserHorseLeagueScore[] }> => {
    const params = new URLSearchParams({ limit: String(limit) })
    if (month) params.set('month', month)
    const res = await apiClient.get<{ leaderboard: UserHorseLeagueScore[] }>(`/horse/league/leaderboard?${params}`)
    return res.data
  },
  myLeagueScore: async (month?: string): Promise<{ score: UserHorseLeagueScore }> => {
    const params = month ? `?month=${month}` : ''
    const res = await apiClient.get<{ score: UserHorseLeagueScore }>(`/horse/league/my-score${params}`)
    return res.data
  },
}

// Admin horse helpers (also attached onto adminApi below via spread usage in AdminPage)
export const horseAdminApi = {
  listCourses: async (limit = 50, offset = 0): Promise<{ courses: HorseCourse[] }> => {
    const res = await apiClient.get<{ courses: HorseCourse[] }>(`/admin/horse/courses?limit=${limit}&offset=${offset}`, {
      headers: adminHeaders(),
    })
    return res.data
  },
  createCourse: async (data: {
    name: string
    start_time: string
    horses: { name: string; number: number; photo_url?: string }[]
  }): Promise<{ course: HorseCourse }> => {
    const res = await apiClient.post<{ course: HorseCourse }>('/admin/horse/courses', data, { headers: adminHeaders() })
    return res.data
  },
  updateCourse: async (
    courseId: number,
    data: {
      name: string
      start_time: string
      horses: { name: string; number: number; photo_url?: string }[]
    },
  ): Promise<{ course: HorseCourse }> => {
    const res = await apiClient.put<{ course: HorseCourse }>(`/admin/horse/courses/${courseId}`, data, {
      headers: adminHeaders(),
    })
    return res.data
  },
  getCourseUsage: async (courseId: number): Promise<{ usage: HorseCourseUsage }> => {
    const res = await apiClient.get<{ usage: HorseCourseUsage }>(
      `/admin/horse/courses/${courseId}/usage`,
      { headers: adminHeaders() },
    )
    return res.data
  },
  deleteCourse: async (courseId: number): Promise<{ ok: boolean }> => {
    const res = await apiClient.delete<{ ok: boolean }>(`/admin/horse/courses/${courseId}`, {
      headers: adminHeaders(),
    })
    return res.data
  },
  setResult: async (courseId: number, positions: Record<number, number>): Promise<{ course: HorseCourse }> => {
    const res = await apiClient.post<{ course: HorseCourse }>(
      `/admin/horse/courses/${courseId}/result`,
      { positions },
      { headers: adminHeaders() },
    )
    return res.data
  },
  listRounds: async (mode?: HorsePollMode): Promise<{ rounds: HorsePollRound[] }> => {
    const params = mode ? `?mode=${mode}` : ''
    const res = await apiClient.get<{ rounds: HorsePollRound[] }>(`/admin/horse/rounds${params}`, {
      headers: adminHeaders(),
    })
    return res.data
  },
  createRound: async (data: {
    mode: HorsePollMode
    title?: string
    course_ids: number[]
    locked_horse_ids?: number[]
  }): Promise<{ round: HorsePollRound }> => {
    const res = await apiClient.post<{ round: HorsePollRound }>('/admin/horse/rounds', data, { headers: adminHeaders() })
    return res.data
  },
  closeRound: async (roundId: number): Promise<{ round: HorsePollRound }> => {
    const res = await apiClient.post<{ round: HorsePollRound }>(
      `/admin/horse/rounds/${roundId}/close`,
      {},
      { headers: adminHeaders() },
    )
    return res.data
  },
  settleRound: async (roundId: number): Promise<{ round: HorsePollRound }> => {
    const res = await apiClient.post<{ round: HorsePollRound }>(
      `/admin/horse/rounds/${roundId}/settle`,
      {},
      { headers: adminHeaders() },
    )
    return res.data
  },
}
